import {
  AgGridEvent,
  Events,
  IStatusPanelComp,
  IStatusPanelParams,
} from '@ag-grid-community/core'
import { loadTemplate } from '@ag-grid-community/core/dist/cjs/utils/dom'
import { debounce } from 'lodash-es'
import { WorkbenchRow } from '../../models/workbench-row.model'

/** Applied to a status panel's root while it has nothing to show, so it drops out of the bar. */
export const EMPTY_PANEL_CLASS = 'wb-status-empty'

/**
 * Markup shared by every workbench status panel. Deliberately reuses the Transactions 1.0 class
 * names (`hs-status-bar` / `hs-transactions-status-bar` / `value-container`) so the panels inherit
 * the global `.ag-status-bar` rules in `apps/helios/src/styles.scss` and read identically to the
 * classic External / Clearwater grids.
 */
export function statusPanelTemplate(
  label: string,
  body: string,
  extraClass: string,
): string {
  return `
    <div class="hs-transactions-status-bar ${extraClass} hs-status-bar">
      <div class="value-container">
        <label>${label}:</label>
        ${body}
      </div>
    </div>
  `
}

/**
 * Base for the workbench status panels that summarise the current row selection.
 *
 * Transactions 1.0 has an equivalent base in `@helios/generic-components`, but it is typed against
 * `GenericTransaction` — a flat `amount` plus `currencyCode`. Workbench rows instead carry
 * per-currency `Money` objects, so the workbench keeps its own selection plumbing while reusing the
 * classic DOM structure, debounce timings and empty-state behaviour.
 */
export abstract class WorkbenchStatusBarBase implements IStatusPanelComp {
  protected readonly eGui: HTMLElement

  private params?: IStatusPanelParams

  /** @internal */
  readonly selectionChangedListener = debounce(
    (event: AgGridEvent) => this.onSelectionChanged(event),
    100,
    { maxWait: 250, trailing: true },
  )

  protected constructor(template: string) {
    this.eGui = loadTemplate(template)
  }

  /**
   * Summarise the current selection into the panel. Implementations own their empty state via
   * {@link setEmptyState} — a panel with nothing to show collapses rather than printing a dash.
   */
  protected abstract render(rows: WorkbenchRow[]): void

  init(params: IStatusPanelParams): void {
    this.params = params
    params.api.addEventListener(
      Events.EVENT_SELECTION_CHANGED,
      this.selectionChangedListener,
    )
  }

  destroy(): void {
    this.params?.api.removeEventListener(
      Events.EVENT_SELECTION_CHANGED,
      this.selectionChangedListener,
    )
  }

  getGui(): HTMLElement {
    this.render(this.selectedRows())
    return this.eGui
  }

  /** @internal */
  onSelectionChanged(event: AgGridEvent): void {
    this.render(event.api.getSelectedRows() as WorkbenchRow[])
  }

  /**
   * Collapse the panel while there is nothing to report. `.value-container.empty` is the classic
   * transaction grids' hook; `EMPTY_PANEL_CLASS` additionally takes the panel root out of layout so
   * the gap between neighbouring panels is never spent on an invisible one.
   */
  protected setEmptyState(isEmpty: boolean): void {
    this.eGui.classList.toggle(EMPTY_PANEL_CLASS, isEmpty)
    const container = this.eGui.getElementsByClassName('value-container')?.[0]
    if (isEmpty) container?.classList.add('empty')
    else container?.classList.remove('empty')
  }

  private selectedRows(): WorkbenchRow[] {
    return (this.params?.api.getSelectedRows() ?? []) as WorkbenchRow[]
  }
}
